import { logger } from "@/infrastructure/services/LoggerService";
/**
 * Authentication Use Cases
 * Contains business logic for authentication operations
 */
export class AuthUseCases {
    constructor(authService) {
        Object.defineProperty(this, "authService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.authService = authService;
    }
    /**
     * Initialize authentication system
     */
    async initializeAuth() {
        try {
            return await this.authService.initialize();
        }
        catch (error) {
            logger.debug("Failed to initialize authentication:", error);
            return false;
        }
    }
    /**
     * Check if user is authenticated
     */
    isUserAuthenticated() {
        return this.authService.isAuthenticated();
    }
    /**
     * Check if authentication is ready
     */
    isAuthReady() {
        return this.authService.isInitialized();
    }
    /**
     * Perform user login with optional redirect URI, login hint, and organization
     */
    async loginUser(redirectUri, loginHint, organization) {
        try {
            await this.authService.login({ redirectUri, loginHint, organization });
        }
        catch (error) {
            logger.debug("Login failed:", error);
            throw new Error("Unable to login. Please try again.");
        }
    }
    /**
     * Perform user logout
     */
    async logoutUser() {
        try {
            await this.authService.logout();
        }
        catch (error) {
            logger.debug("Logout failed:", error);
            throw new Error("Unable to logout. Please try again.");
        }
    }
    /**
     * Get current user information
     */
    getCurrentUser() {
        try {
            return this.authService.getUserInfo();
        }
        catch (error) {
            logger.debug("Failed to get user info:", error);
            return null;
        }
    }
    /**
     * Get current access token
     */
    getAccessToken() {
        try {
            return this.authService.getToken();
        }
        catch (error) {
            logger.debug("Failed to get access token:", error);
            return undefined;
        }
    }
    /**
     * Check if user has required role
     */
    hasUserRole(role) {
        try {
            return this.authService.hasRole(role);
        }
        catch (error) {
            logger.debug("Failed to check user role:", error);
            return false;
        }
    }
    /**
     * Check if user has any of the required roles
     */
    hasAnyUserRole(roles) {
        try {
            return this.authService.hasAnyRole(roles);
        }
        catch (error) {
            logger.debug("Failed to check user roles:", error);
            return false;
        }
    }
    /**
     * Refresh authentication token
     */
    async refreshAuthToken() {
        try {
            return await this.authService.refreshToken();
        }
        catch (error) {
            logger.debug("Token refresh failed:", error);
            return false;
        }
    }
    /**
     * Force refresh the authentication token
     * Useful when token claims need to be updated (e.g., after locale change)
     */
    async forceRefreshAuthToken() {
        try {
            return await this.authService.forceRefreshToken();
        }
        catch (error) {
            logger.debug("Force token refresh failed:", error);
            return false;
        }
    }
    /**
     * Check if token needs refresh
     */
    shouldRefreshToken(minValidity = 300) {
        try {
            return this.authService.isTokenExpired(minValidity);
        }
        catch (error) {
            logger.debug("Failed to check token expiry:", error);
            return true; // Assume token is expired on error
        }
    }
    /**
     * Reinitialize authentication system
     */
    async reinitializeAuth() {
        try {
            await this.authService.reinitialize();
        }
        catch (error) {
            logger.debug("Failed to reinitialize authentication:", error);
            throw new Error("Unable to reinitialize authentication system.");
        }
    }
    /**
     * Get user display name (preferred username or name)
     */
    getUserDisplayName() {
        const user = this.getCurrentUser();
        if (!user)
            return "Anonymous";
        return user.preferred_username || user.name || user.email || "User";
    }
    /**
     * Check if user email is verified
     */
    isUserEmailVerified() {
        const user = this.getCurrentUser();
        return user?.email_verified ?? false;
    }
    /**
     * Get user roles
     */
    getUserRoles() {
        try {
            return this.authService.getUserRoles();
        }
        catch (error) {
            logger.debug("Failed to get user roles:", error);
            return [];
        }
    }
}
